import React, { useState, useEffect, useCallback } from "react";
import { Table, Input, Select, Button, Card, Space, Modal, Typography, message, DatePicker } from "antd";
import { CSVLink } from "react-csv";
import { EditOutlined, VideoCameraOutlined, EnvironmentOutlined, HomeOutlined, QuestionOutlined, CalendarOutlined } from '@ant-design/icons';
import axios from "axios";
import withRoleAccess from "../../hoc/withRoleAccess";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faSearch,
  faCalendarAlt,
  faChalkboardTeacher,
  faBookOpen
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import "./Classes.css";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const AllClassList = () => {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editedTeacherId, setEditedTeacherId] = useState('');
  const [editingDateId, setEditingDateId] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editedSubjectId, setEditedSubjectId] = useState('');
  const [editingPlatformId, setEditingPlatformId] = useState(null);
  const [editedPlatform, setEditedPlatform] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [editedTime, setEditedTime] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD")
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0
  });

  const API_URL = process.env.REACT_APP_API_URL;

const formatDate = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(date);
};


  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const formatScheduleHour = (scheduleHourString) => {
    if (!scheduleHourString) return "-";
    try {
      const [hours, minutes] = scheduleHourString.split(' ')
        .map((value, index) => (index === 0 ? parseInt(value) : parseInt(value)));
      if (isNaN(hours) || isNaN(minutes)) return scheduleHourString;
      return `${hours}.${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Error formatting schedule hour:', error);
      return scheduleHourString;
    }
  };



  const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
    const hour = 6 + Math.floor(index / 4);
    const minutes = (index % 4) * 15;
    const label = `${hour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
    return { label, value: label + ":00" }; // stored as HH:mm:ss
  });
  

const fetchClasses = useCallback(async () => {
  if (!dateRange?.startDate || !dateRange?.endDate) {
    console.warn("⚠️ Missing dateRange when calling fetchClasses");
    return;
  }

  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("❌ No token found");
      setLoading(false);
      return;
    }

    const response = await axios.get(`${API_URL}/classes/class-by-day`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      },
    });

    setClasses(response.data);
  } catch (error) {
    console.error("❌ Error fetching classes:", error);
  } finally {
    setLoading(false);
  }
}, [API_URL, dateRange]);

// Fetch teachers
const fetchTeachers = useCallback(async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/teachers`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // Access the teachers array inside response.data
    const teachersArray = response.data.teachers || [];
    setTeachers(teachersArray.filter((teacher) => teacher.status === "active"));
  } catch (error) {
    console.error("Error fetching teachers:", error);
  }
}, [API_URL]);


// Fetch subjects
const fetchSubjects = useCallback(async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/subjects`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setSubjects(response.data);
  } catch (error) {
    console.error("Error fetching subjects:", error);
  }
}, [API_URL]);

useEffect(() => {
  fetchClasses();
  fetchTeachers();
  fetchSubjects(); 
}, [fetchClasses, fetchTeachers, fetchSubjects]);


  const handleEditDateClick = (classId, date) => {
    setEditingDateId(classId);
    setEditedDate(formatDate(date)); 
  };  

  const handleTeacherChangeDropdown = (value) => {
    setEditedTeacherId(value);
  };

  const handleEditTeacherClick = (classId, teacherId) => {
    setEditingTeacherId(classId);
    setEditedTeacherId(teacherId); 
  };

  const handleSubjectChangeDropdown = (value) => {
    setEditedSubjectId(value);
  };
  
  const handleEditSubjectClick = (classId, subjectId) => {
    setEditingSubjectId(classId);
    setEditedSubjectId(subjectId); 
  };

  const showConfirmBeforeSave = ({ onOk, title = "Are you sure?", content = "Do you want to save the changes?" }) => {
    Modal.confirm({
      title: title,
      content: content,
      okText: 'Yes',
      cancelText: 'No',
      onOk, // if user clicks yes, execute the onOk function
    });
  };
  

  const handleSaveTeacherClick = (classId) => {
    showConfirmBeforeSave({
      title: "Confirm Teacher Update",
      content: "Are you sure you want to update the teacher?",
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const updatedClass = {
            teacher_id: editedTeacherId,
          };
    
          await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
            headers: { Authorization: `Bearer ${token}` }
          });
    
          setEditingTeacherId(null);
          setEditedTeacherId('');
          fetchClasses();
        } catch (error) {
          console.error('Error updating teacher:', error);
        }
      }
    });
  };
  

  const handleSaveDateClick = (classId) => {
    showConfirmBeforeSave({
      title: "Confirm Date Update",
      content: "Are you sure you want to update the date?",
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const updatedClass = {
            date: editedDate,
          };
    
          await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
            headers: { Authorization: `Bearer ${token}` }
          });
    
          setEditingDateId(null);
          setEditedDate('');
          fetchClasses();
        } catch (error) {
          console.error('Error updating date:', error);
        }
      }
    });
  };
  

  const handleSaveSubjectClick = (classId) => {
    showConfirmBeforeSave({
      title: "Confirm Subject Update",
      content: "Are you sure you want to update the subject?",
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const updatedClass = {
            subjectid: editedSubjectId,
          };
    
          await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
            headers: { Authorization: `Bearer ${token}` }
          });
    
          setEditingSubjectId(null);
          setEditedSubjectId('');
          fetchClasses();
        } catch (error) {
          console.error('Error updating subject:', error);
        }
      }
    });
  };

  const handleSaveTime = (classId) => {
  showConfirmBeforeSave({
    title: "Confirm Time Update",
    content: "Are you sure you want to update start time?",
    onOk: async () => {
      try {
        const token = localStorage.getItem("token");
        await axios.put(`${API_URL}/classes/time-change/${classId}`, {
          schedule_time: editedTime
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setEditingTimeId(null);
        setEditedTime("");
        fetchClasses();
      } catch (error) {
        console.error("❌ Error updating time:", error);
      }
    }
  });
};

// ✨ Start editing
const handleEditPlatformClick = (classId, platform) => {
  setEditingPlatformId(classId);
  setEditedPlatform(platform);
};

// ✨ Save platform
const handleSavePlatformClick = (classId) => {
  showConfirmBeforeSave({
    title: "Confirm Platform Update",
    content: "Are you sure you want to update the platform?",
    onOk: async () => {
      try {
        const token = localStorage.getItem("token");

        await axios.put(
          `${API_URL}/classes/${classId}`,
          { platform: editedPlatform },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        message.success("Platform updated successfully");
        setEditingPlatformId(null);
        setEditedPlatform(null);
        fetchClasses();
      } catch (error) {
        console.error("❌ Error updating platform:", error);
        message.error("Failed to update platform");
      }
    },
  });
};
  
  const filteredClasses = classes
  .filter((classItem) => {
    // Convert class date to Bangkok time
    const classDate = moment(classItem.date).utcOffset('+07:00');
    const startDate = moment(dateRange.startDate).utcOffset('+07:00');
    const endDate = moment(dateRange.endDate).utcOffset('+07:00');
    
    const isInDateRange = 
      classDate.isSameOrAfter(startDate, 'day') && 
      classDate.isSameOrBefore(endDate, 'day');
    
    const isSearchMatch =
      classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const isTeacherMatch =
      !selectedTeacher || classItem.teacher_id === selectedTeacher;

    return isInDateRange && isSearchMatch && isTeacherMatch;
  })
  .sort((a, b) => {
    // Sort by date and then by schedule_time
    const dateComparison = new Date(a.date) - new Date(b.date);
    if (dateComparison !== 0) {
      return dateComparison;
    }
    return moment(a.schedule_time, "HH:mm:ss").diff(
      moment(b.schedule_time, "HH:mm:ss")
    );
  });

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: filteredClasses.length
    }));
  }, [filteredClasses]);

  const handleDateRangeChange = (dates) => {
    setDateRange({
      startDate: dates ? dates[0].format('YYYY-MM-DD') : null,
      endDate: dates ? dates[1].format('YYYY-MM-DD') : null,
    });
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const getCurrentPageData = () => {
    const { current, pageSize } = pagination;
    const startIndex = (current - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClasses.slice(startIndex, endIndex);
  };

  // CSV Data for Export
const csvData = filteredClasses.map((classItem) => ({
  Date: formatDate(classItem.date),

  Day: new Date(classItem.date).toLocaleDateString("en-US", { 
    weekday: "long" 
  }),

  "Student Name (course)": classItem.class_code,

  "Start Time": formatTime(classItem.schedule_time),
  "End Time": formatTime(classItem.end_time),

  Hours: formatScheduleHour(classItem.schedule_hour),

  Subject: classItem.subject_name,

  Tutor:
    teachers.find((teacher) => teacher.userid === classItem.teacher_id)
      ?.nickname || "N/A",

  Type: classItem.isprivate ? "Private" : "Group",

  Platform:
    classItem.platform === "online"
      ? "Online"
      : classItem.platform === "onsite"
      ? "Onsite"
      : classItem.platform === "athome"
      ? "At Home"
      : "N/A",

  // NEW — approval field replacing status
  "Admin Approval":
    classItem.adminapprove === "adminconfirm"
      ? "Confirmed"
      : classItem.adminapprove || "Pending",
}));


  const columns = [
   {
  title: t("Date"),
  dataIndex: "date",
  key: "date",
  width: 160,
  ellipsis: true,
  render: (date, record) => {
    return editingDateId === record.classid && record.adminapprove !== 'adminconfirm' ? (
      <Space direction="vertical" style={{ width: '100%' }}>
        <input
          type="date"
          value={editedDate}
          onChange={(e) => setEditedDate(e.target.value)}
          style={{
            width: '100%',
            padding: '4px 11px',
            border: '1px solid #d9d9d9',
            borderRadius: '2px',
            fontSize: '14px'
          }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSaveDateClick(record.classid)}
          >
            {t("Save")}
          </Button>
          <Button
            size="small"
            onClick={() => setEditingDateId(null)}
          >
            {t("Cancel")}
          </Button>
        </Space>
      </Space>
    ) : (
      <Space>
        <Button
          type="link"
          icon={<FontAwesomeIcon icon={faCalendarAlt} />}
          onClick={() => {
            if (record.adminapprove !== 'adminconfirm') {
              handleEditDateClick(record.classid, record.date);
            }
          }}
          style={{ padding: '0', height: 'auto' }}
          disabled={record.adminapprove === 'adminconfirm'} 
        />
        <span>{moment(date).format("DD MMM YY")}</span>
      </Space>
    );
  },
},   
    {
      title: t("Day"),
      dataIndex: "day",
      key: "day",
      width: 100,
      render: (_, record) => (
        <span className="day-cell">
          {new Date(record.date).toLocaleDateString("en-US", { weekday: "long" })}
        </span>
      ),
    },
    {
  title: t("Time"),
  key: "time",
  width: 160,
  render: (_, record) => {
    return editingTimeId === record.classid && record.adminapprove !== 'adminconfirm' ? (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          value={editedTime || record.schedule_time}
          onChange={setEditedTime}
          style={{ width: 120 }}
          options={timeOptions}
          showSearch
        />
        <Space>
          <Button size="small" type="primary" onClick={() => handleSaveTime(record.classid)}>
            {t("Save")}
          </Button>
          <Button size="small" onClick={() => setEditingTimeId(null)}>
            {t("Cancel")}
          </Button>
        </Space>
      </Space>
    ) : (
      <Space>
        {record.adminapprove !== 'adminconfirm' && ( 
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingTimeId(record.classid);
              setEditedTime(record.schedule_time);
            }}
          />
        )}
        <span>
          {moment(record.schedule_time, "HH:mm:ss").format("HH:mm")} -{" "}
          {moment(record.end_time, "HH:mm:ss").format("HH:mm")}
        </span>
      </Space>
    );
  }
},
    {
      title: t("Hours"),
      dataIndex: "schedule_hour",
      key: "schedule_hour",
      width: 80,
      align: "center",
      render: (scheduleHour) => {
        if (!scheduleHour) return "-";
        const formattedHour = formatScheduleHour(scheduleHour);
        return <span>{formattedHour} {t('hr')}</span>;
      },
    },
        {
      title: t("classcode"),
      dataIndex: "class_code",
      key: "class_code",
      width: 250,
      render: (text) => <div className="wrapped-cell">{text}</div>,
    },
    {
      title: t("Type"),
      dataIndex: "isprivate",
      key: "isprivate",
      width: 90,
      align: "center",
      render: (isPrivate) => (
        <span
          style={{
            display: "inline-block",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            lineHeight: 1,
            whiteSpace: "nowrap",
            background: isPrivate ? "#e6f4ff" : "#f6ffed",
            color: isPrivate ? "#1890ff" : "#52c41a",
            border: `1px solid ${isPrivate ? "#91caff" : "#b7eb8f"}`,
          }}
        >
          {isPrivate ? t("Private") : t("Group")}
        </span>
      ),
    },
{
  title: t("platform"),
  dataIndex: "platform",
  key: "platform",
  width: 140,
  render: (platform, record) => {
    const isEditing = editingPlatformId === record.classid;
    const isLocked = record.adminapprove === "adminconfirm"; // 🔥 APPROVED → LOCKED

    const PLATFORM_OPTIONS = [
      { value: "online", label: t("online"), icon: <VideoCameraOutlined /> },
      { value: "onsite", label: t("onsite"), icon: <EnvironmentOutlined /> },
      { value: "athome", label: t("athome"), icon: <HomeOutlined /> },
    ];

    const getPlatformConfig = (p) => {
      switch (p) {
        case "online":
          return { text: t("online"), icon: <VideoCameraOutlined />, bg: "#E6F4FF", border: "#91CAFF", color: "#0958D9" };
        case "onsite":
          return { text: t("onsite"), icon: <EnvironmentOutlined />, bg: "#F6FFED", border: "#B7EB8F", color: "#389E0D" };
        case "athome":
          return { text: t("athome"), icon: <HomeOutlined />, bg: "#FFF7E6", border: "#FFD591", color: "#D48806" };
        default:
          return { text: p || "-", icon: <QuestionOutlined />, bg: "#F0F0F0", border: "#D9D9D9", color: "#595959" };
      }
    };

    const config = getPlatformConfig(platform);

    // 🔹 VIEW MODE
    if (!isEditing) {
      return (
        <span
          onClick={() => {
            if (!isLocked) {
              handleEditPlatformClick(record.classid, platform);
            }
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px",
            borderRadius: 6,
            fontSize: 12,
            background: config.bg,
            border: `1px solid ${config.border}`,
            color: config.color,
            cursor: isLocked ? "not-allowed" : "pointer",
            opacity: isLocked ? 0.6 : 1, // 🔒 FADE IF LOCKED
          }}
        >
          {config.icon} {config.text}
        </span>
      );
    }

    // 🔹 EDIT MODE
    return (
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          value={editedPlatform}
          onChange={setEditedPlatform}
          style={{ width: "100%" }}
          disabled={isLocked} // 🔥 LOCKED → CANNOT EDIT
        >
          {PLATFORM_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.icon} {opt.label}
            </Option>
          ))}
        </Select>

        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSavePlatformClick(record.classid)}
            disabled={isLocked} // 🔥 LOCKED → DISABLE SAVE
          >
            {t("Save")}
          </Button>

          <Button size="small" onClick={() => setEditingPlatformId(null)}>
            {t("Cancel")}
          </Button>
        </Space>
      </Space>
    );
  },
},


    {
      title: t("Subject"),
      dataIndex: "subject_name",
      key: "subject_name",
      width: 250,
      render: (text, record) => {
        return editingSubjectId === record.classid && record.adminapprove !== 'adminconfirm' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              value={editedSubjectId || undefined}
              onChange={handleSubjectChangeDropdown}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subjects.map((subject) => (
                <Option key={subject.subjectid} value={subject.subjectid}>
                  {subject.title}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveSubjectClick(record.classid)}
              >
                {t("Save")}
              </Button>
              <Button
                size="small"
                onClick={() => setEditingSubjectId(null)}
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Space>
        ) : (
          <Space>
            {/* Swap: Button first, then Subject Name */}
            {record.adminapprove !== 'adminconfirm' &&  ( // 🔥 Only show edit button if not approved
              <Button
                type="link"
                icon={<FontAwesomeIcon icon={faBookOpen} />}
                onClick={() => handleEditSubjectClick(record.classid, record.subject_id)}
                style={{ padding: '0', height: 'auto' }}
              />
            )}
            <span>{text || t("N/A")}</span>
          </Space>
        );
      },
    },
    {
      title: t("Tutor"),
      dataIndex: "teacher_id",
      key: "teacher_id",
      width: 180,
      render: (teacherId, record) => {
        return editingTeacherId === record.classid && record.adminapprove !== 'adminconfirm' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              value={editedTeacherId || undefined}
              onChange={handleTeacherChangeDropdown}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {teachers.map((teacher) => (
                <Option
                  key={teacher.userid}
                  value={teacher.userid}
                  label={`${teacher.nickname} ${teacher.firstname}`}
                >
                  {teacher.nickname} {teacher.firstname}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveTeacherClick(record.classid)}
              >
                {t("Save")}
              </Button>
              <Button
                size="small"
                onClick={() => setEditingTeacherId(null)}
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Space>
        ) : (
          <Space>
            {/* Button first, then tutor name */}
            {record.adminapprove !== 'adminconfirm' && ( // 🔥 Only show edit button if status is not approved
              <Button
                type="link"
                icon={<FontAwesomeIcon icon={faChalkboardTeacher} />}
                onClick={() => handleEditTeacherClick(record.classid, record.teacher_id)}
                style={{ padding: '0', height: 'auto' }}
              />
            )}
            <span>
              {teachers.find((teacher) => teacher.userid === teacherId)?.nickname || t("N/A")}
            </span>
          </Space>
        );
      },
    },    
 {
  title: t("Status"),
  dataIndex: "adminapprove",
  key: "adminapprove",
  width: 100,
  align: "center",
  render: (adminapprove) => (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        lineHeight: 1,
        whiteSpace: "nowrap",
        background: getStatusBackground(adminapprove),
        color: getStatusColor(adminapprove),
        border: `1px solid ${getStatusBorderColor(adminapprove)}`,
        textTransform: "capitalize",
      }}
    >
      {adminapprove === "adminconfirm" ? "confirmed" : adminapprove}

    </span>
  ),
},

  ];

  // Helper functions for status styling
  const getStatusBackground = (status) => {
    switch(status?.toLowerCase()) {
      case 'adminconfirm': return '#f6ffed';
      case 'pending': return '#fff7e6';
      case 'cancelled': return '#fff1f0';
      default: return '#f5f5f5';
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'adminconfirm': return '#52c41a';
      case 'pending': return '#fa8c16';
      case 'cancelled': return '#f5222d';
      default: return '#8c8c8c';
    }
  };

  const getStatusBorderColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'adminconfirm': return '#b7eb8f';
      case 'pending': return '#ffd591';
      case 'cancelled': return '#ffa39e';
      default: return '#d9d9d9';
    }
  };

  return (
    <Card className="class-list-container">
      <div className="header-section">
      </div>

      <div className="cc-search-container">
          {/* Date Picker */}
                  <Space>
              <RangePicker onChange={handleDateRangeChange} />

          <Input
            prefix={
              <FontAwesomeIcon icon={faSearch} style={{ color: "#bfbfbf" }} />
            }
            placeholder={t("Search by subject or student name (course)")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            showSearch
            placeholder={t("Filter by teacher")}
            value={selectedTeacher}
            onChange={(value) => setSelectedTeacher(value)}
            style={{ width: 200 }}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            <Option value="" label={t("All Teachers")}>{t("All Teachers")}</Option>
            {teachers.map((teacher) => (
              <Option key={teacher.userid} value={teacher.userid} label={`${teacher.nickname} ${teacher.firstname}`}>
                {teacher.nickname} {teacher.firstname}
              </Option>
            ))}
          </Select>

          <CSVLink
            data={csvData}
            filename={`class_list_${moment().format("YYYY-MM-DD")}.csv`}
          >
            <Button
              type="primary"
              icon={<FontAwesomeIcon icon={faFileExcel} />}
            >
              {t("Export CSV")}
            </Button>
          </CSVLink>
        </Space>
      </div>

      <Table
        dataSource={getCurrentPageData()}
        columns={columns}
        rowKey="classid"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["50", "100", "200"],
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`
        }}
        onChange={handleTableChange}
        scroll={{ x: '100%' }}
        size="middle"
      />
    </Card>
  );
};

export default withRoleAccess(AllClassList, ["admin", "superadmin"]);